/**
 * D13 + D23: Skill Workflows Page
 * ===========================
 * 4-tab view: Dashboard | Definitions | Instances | Test Runner (admin-only)
 */
import React, { useState, useEffect, useCallback } from 'react';
import useTabState from '../hooks/useTabState';
import { useApp } from '../contexts/AppContext';
import { useToast } from '../contexts/ToastContext';
import api from '../api';
import { realtimeService } from '../services/realtimeService';
import WorkflowCanvas from '../components/WorkflowCanvas';
import WorkflowBuilder from '../components/WorkflowBuilder';
import { DetailSheet } from '../components/finance/DetailSheet';
import StatusBadge from '../components/ui/StatusBadge';
import { DetailSkeleton } from '../components/finance/SkeletonStates';
import PageLayout from '../components/ui/PageLayout';
import TabBar from '../components/ui/TabBar';
import KpiGrid from '../components/ui/KpiGrid';
import FilterBar from '../components/ui/FilterBar';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';

// --- Types ---

interface WorkflowDefinition {
  id: number;
  workflow_code: string;
  workflow_name: string;
  department: string;
  sub_department: string | null;
  description: string;
  trigger_config: any;
  skill_sequence: any[];
  estimated_duration_seconds: number | null;
  status: string;
}

interface WorkflowInstance {
  id: number;
  org_id: number;
  workflow_id: number;
  transaction_id: string;
  transaction_type: string;
  current_step: number;
  status: string;
  step_results: any[];
  config_snapshot: any;
  started_at: string | null;
  completed_at: string | null;
  paused_at: string | null;
  paused_reason: string | null;
  entity_code: string | null;
  priority: string;
  error_detail: string | null;
  workflow_code?: string;
  workflow_name?: string;
}

interface Metrics {
  total_instances: number;
  by_status: Record<string, number>;
  avg_cycle_seconds: number;
  auto_complete_rate: number;
}

interface ExecutionLog {
  id: number;
  step_number: number;
  skill_id: number;
  skill_code: string;
  execution_mode: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  execution_ms: number | null;
  ai_tokens_used: number | null;
  input_summary: any;
  output_summary: any;
  error_detail: string | null;
}

// --- Constants ---

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  COMPLETED: { bg: 'rgba(16,185,129,0.15)', color: '#6ee7b7' },
  RUNNING: { bg: 'var(--accent-subtle)', color: 'var(--navy-700)' },
  PAUSED_HUMAN: { bg: '#fffbeb', color: '#fcd34d' },
  FAILED: { bg: '#fef2f2', color: '#fca5a5' },
  CANCELLED: { bg: 'var(--bg-elevated)', color: '#9ca3af' },
  SKIPPED: { bg: 'var(--bg-elevated)', color: '#94a3b8' },
};

const STEP_ICONS: Record<string, string> = {
  SUCCESS: '\u2705',
  COMPLETED: '\u2705',
  FAILED: '\u274C',
  SKIPPED: '\u23ED',
  EXECUTING: '\u23F3',
};

const STATUS_LABELS: Record<string, string> = {
  PAUSED_HUMAN: 'Awaiting review',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  RUNNING: 'Running',
  SKIPPED: 'Skipped',
  CANCELLED: 'Cancelled',
};

const TRIGGER_STYLES: Record<string, { bg: string; color: string }> = {
  DATA_UPLOAD: { bg: 'rgba(6,182,212,0.12)', color: 'var(--accent)' },
  SCHEDULED: { bg: '#fffbeb', color: '#F59E0B' },
  SCHEDULE: { bg: '#fffbeb', color: '#F59E0B' },
  MANUAL: { bg: 'rgba(100,116,139,0.12)', color: '#94A3B8' },
  EVENT: { bg: 'rgba(139,92,246,0.12)', color: '#A78BFA' },
};

function cleanDeptName(dept: string): string {
  return dept.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return '--';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function cleanWorkflowName(code: string): string {
  return code.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// --- Component ---

export default function SkillWorkflows() {
  const { hasPermission, authUser } = useApp();
  const { toast } = useToast();

  type Tab = 'Dashboard' | 'Definitions' | 'Builder' | 'Instances' | 'Test Runner';
  const ALL_TABS: readonly Tab[] = ['Dashboard', 'Definitions', 'Builder', 'Instances', 'Test Runner'];
  const [activeTab, setActiveTab] = useTabState<Tab>('tab', ALL_TABS, 'Dashboard');
  const [showBuilder, setShowBuilder] = useState(false);

  // Dashboard
  const [metrics, setMetrics] = useState<Metrics | null>(null);

  // Definitions
  const [definitions, setDefinitions] = useState<WorkflowDefinition[]>([]);
  const [defSearch, setDefSearch] = useState('');
  const [defDept, setDefDept] = useState('');
  const [expandedDef, setExpandedDef] = useState<number | null>(null);
  const [collapsedDepts, setCollapsedDepts] = useState<Set<string>>(new Set());
  const [canvasWorkflow, setCanvasWorkflow] = useState<WorkflowDefinition | null>(null);

  // Instances
  const [instances, setInstances] = useState<WorkflowInstance[]>([]);
  const [instStatus, setInstStatus] = useState('');
  const [instSearch, setInstSearch] = useState('');
  const [expandedInst, setExpandedInst] = useState<number | null>(null);
  const [instDetail, setInstDetail] = useState<WorkflowInstance | null>(null);
  const [execLogs, setExecLogs] = useState<ExecutionLog[]>([]);
  const [wfPanelId, setWfPanelId] = useState<number | null>(null);
  const [wfPanelData, setWfPanelData] = useState<WorkflowInstance | null>(null);
  const [wfPanelLogs, setWfPanelLogs] = useState<ExecutionLog[]>([]);
  const [wfPanelLoading, setWfPanelLoading] = useState(false);

  // Shared
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Admin gate for Test Runner
  const isAdmin = authUser?.role === 'ADMIN';

  const TABS: Tab[] = isAdmin
    ? ['Dashboard', 'Definitions', 'Builder', 'Instances', 'Test Runner']
    : ['Dashboard', 'Definitions', 'Builder', 'Instances'];

  // --- Data Loading ---

  const loadMetrics = useCallback(async () => {
    try {
      const res = await api.get('/skill-workflows/metrics', { params: { org_id: authUser?.organization_id || 1 } });
      setMetrics(res.data);
    } catch (e) {
      console.error('Failed to load metrics:', e);
      toast({ message: 'Failed to load workflow metrics.', type: 'error' });
    }
  }, [authUser?.organization_id]);

  const loadDefinitions = useCallback(async () => {
    try {
      const res = await api.get('/skill-workflows/definitions', { params: { org_id: authUser?.organization_id || 1 } });
      setDefinitions(res.data.definitions || []);
    } catch (e) {
      console.error('Failed to load definitions:', e);
      toast({ message: 'Failed to load workflow definitions.', type: 'error' });
    }
  }, [authUser?.organization_id]);

  const loadInstances = useCallback(async () => {
    try {
      const params: any = { org_id: authUser?.organization_id || 1, limit: 100 };
      if (instStatus) params.status = instStatus;
      const res = await api.get('/skill-workflows/instances', { params });
      setInstances(res.data.instances || []);
    } catch (e) {
      console.error('Failed to load instances:', e);
      toast({ message: 'Failed to load workflow instances.', type: 'error' });
    }
  }, [instStatus, authUser?.organization_id]);

  const loadInstanceDetail = useCallback(async (id: number) => {
    try {
      const [detailRes, logsRes] = await Promise.all([
        api.get(`/skill-workflows/instances/${id}`, { params: { org_id: authUser?.organization_id || 1 } }),
        api.get(`/skill-workflows/execution-logs/${id}`, { params: { org_id: authUser?.organization_id || 1 } }),
      ]);
      setInstDetail(detailRes.data);
      setExecLogs(logsRes.data.logs || []);
    } catch (e) {
      console.error('Failed to load instance detail:', e);
      toast({ message: 'Failed to load instance details.', type: 'error' });
    }
  }, [authUser?.organization_id]);

  const openWfPanel = useCallback(async (id: number) => {
    setWfPanelId(id);
    setWfPanelLoading(true);
    try {
      const [detailRes, logsRes] = await Promise.all([
        api.get(`/skill-workflows/instances/${id}`, { params: { org_id: authUser?.organization_id || 1 } }),
        api.get(`/skill-workflows/execution-logs/${id}`, { params: { org_id: authUser?.organization_id || 1 } }),
      ]);
      setWfPanelData(detailRes.data);
      setWfPanelLogs(logsRes.data.logs || []);
    } catch {
      toast({ message: 'Failed to load workflow panel data.', type: 'error' });
      setWfPanelData(null);
      setWfPanelLogs([]);
    } finally {
      setWfPanelLoading(false);
    }
  }, [authUser?.organization_id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadMetrics(), loadDefinitions(), loadInstances()])
      .finally(() => setLoading(false));
  }, [loadMetrics, loadDefinitions, loadInstances]);

  // SSE
  useEffect(() => {
    if (activeTab !== 'Instances') return;
    const unsub = realtimeService.on('agent_update', () => { loadInstances(); });
    return () => { unsub(); };
  }, [activeTab, loadInstances]);

  // Initialize collapsed departments (collapse all beyond first 3)
  useEffect(() => {
    if (definitions.length === 0) return;
    const deptCounts: Record<string, number> = {};
    for (const d of definitions) { deptCounts[d.department] = (deptCounts[d.department] || 0) + 1; }
    const sorted = Object.entries(deptCounts).sort((a, b) => b[1] - a[1]);
    const collapsed = new Set<string>();
    sorted.forEach(([dept], i) => { if (i >= 3) collapsed.add(dept); });
    setCollapsedDepts(collapsed);
  }, [definitions]);

  // --- Render ---

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-secondary)' }}>Loading workflows...</div>;
  }

  // Departments for filter
  const departments = Array.from(new Set(definitions.map(d => d.department))).sort();

  // Department counts (sorted by count desc)
  const deptCounts: Record<string, number> = {};
  for (const d of definitions) { deptCounts[d.department] = (deptCounts[d.department] || 0) + 1; }
  const sortedDepts = Object.entries(deptCounts).sort((a, b) => b[1] - a[1]);

  // Filtered definitions
  const filteredDefs = definitions.filter(d => {
    if (defDept && d.department !== defDept) return false;
    if (defSearch) {
      const q = defSearch.toLowerCase();
      return d.workflow_code.toLowerCase().includes(q) || d.workflow_name.toLowerCase().includes(q);
    }
    return true;
  });

  // Group filtered definitions by department
  const groupedDefs: Record<string, WorkflowDefinition[]> = {};
  for (const d of filteredDefs) {
    if (!groupedDefs[d.department]) groupedDefs[d.department] = [];
    groupedDefs[d.department].push(d);
  }

  // Build workflow name lookup for instances
  const wfNameMap: Record<number, { name: string; dept: string }> = {};
  for (const d of definitions) {
    wfNameMap[d.id] = { name: d.workflow_name, dept: d.department };
  }

  // Filtered instances
  const filteredInst = instances.filter(inst => {
    if (instSearch) {
      const q = instSearch.toLowerCase();
      return (inst.workflow_code || '').toLowerCase().includes(q) ||
             (inst.workflow_name || '').toLowerCase().includes(q) ||
             (inst.transaction_id || '').toLowerCase().includes(q) ||
             String(inst.id).includes(q);
    }
    return true;
  });

  const toggleDept = (dept: string) => {
    setCollapsedDepts(prev => {
      const next = new Set(prev);
      if (next.has(dept)) next.delete(dept); else next.add(dept);
      return next;
    });
  };

  // Navigation helpers for dashboard clicks
  const goToDefsDept = (dept: string) => { setActiveTab('Definitions'); setDefDept(dept); setDefSearch(''); };
  const goToInstStatus = (status: string) => { setActiveTab('Instances'); setInstStatus(status); setInstSearch(''); loadInstances(); };

  // Instance table columns
  const instanceCols: Column[] = [
    { key: 'id', label: 'ID', render: (v: number) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-hint)' }}>#{v}</span> },
    { key: 'workflow_name', label: 'Workflow', render: (_: any, row: WorkflowInstance) => {
      const wfInfo = wfNameMap[row.workflow_id];
      return <span style={{ fontWeight: 600 }}>{row.workflow_name || wfInfo?.name || cleanWorkflowName(row.workflow_code || `WF-${row.workflow_id}`)}</span>;
    }},
    { key: 'department', label: 'Department', render: (_: any, row: WorkflowInstance) => {
      const dept = wfNameMap[row.workflow_id]?.dept || '';
      return dept ? <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{cleanDeptName(dept)}</span> : <span style={{ fontSize: 10, color: 'var(--text-hint)' }}>--</span>;
    }},
    { key: 'status', label: 'Status', align: 'center', render: (_: any, row: WorkflowInstance) => {
      const sc = STATUS_COLORS[row.status] || STATUS_COLORS.CANCELLED;
      return <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, fontWeight: 600, background: sc.bg, color: sc.color, ...(row.status === 'RUNNING' ? { animation: 'pulse 2s infinite' } : {}) }}>{STATUS_LABELS[row.status] || row.status}</span>;
    }},
    { key: 'current_step', label: 'Step', align: 'center', render: (_: any, row: WorkflowInstance) => {
      const totalSteps = row.step_results?.length || row.current_step || 1;
      return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)' }}>{row.current_step}/{totalSteps}</span>;
    }},
    { key: 'started_at', label: 'Started', render: (v: string | null) => <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{v ? timeAgo(v) : '--'}</span> },
    { key: 'duration', label: 'Duration', align: 'right', render: (_: any, row: WorkflowInstance) => {
      const started = row.started_at ? new Date(row.started_at) : null;
      const ended = row.completed_at ? new Date(row.completed_at) : null;
      const durationMs = started && ended ? ended.getTime() - started.getTime() : null;
      return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)' }}>{durationMs !== null ? `${(durationMs / 1000).toFixed(1)}s` : '--'}</span>;
    }},
  ];

  return (
    <PageLayout
      title="Skill Workflows"
      subtitle={`Workflow orchestration engine -- ${definitions.length} definitions, ${metrics?.total_instances || 0} instances`}
    >
      {/* Error */}
      {error && (
        <div style={{ padding: '8px 16px', marginBottom: 16, background: 'rgba(239,68,68,0.1)', borderRadius: 6, fontSize: 13, color: 'var(--red)' }}>
          {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer' }}>X</button>
        </div>
      )}

      {/* Tab bar */}
      <TabBar
        tabs={TABS.map(tab => {
          if (tab === 'Instances' && metrics && metrics.by_status?.PAUSED_HUMAN) {
            return { key: tab, label: tab, count: metrics.by_status.PAUSED_HUMAN };
          }
          return { key: tab, label: tab };
        })}
        active={activeTab}
        onChange={v => setActiveTab(v as Tab)}
      />

      {/* DASHBOARD TAB */}
      {activeTab === 'Dashboard' && metrics && (
        <>
          <KpiGrid columns={6} items={[
            { label: 'Total Instances', value: metrics.total_instances, color: 'var(--accent)' },
            { label: 'Running', value: metrics.by_status?.RUNNING || 0, color: 'var(--navy-700)', onClick: () => goToInstStatus('RUNNING') },
            { label: 'Awaiting Review', value: metrics.by_status?.PAUSED_HUMAN || 0, color: '#f59e0b', onClick: () => goToInstStatus('PAUSED_HUMAN') },
            { label: 'Completed', value: metrics.by_status?.COMPLETED || 0, color: '#10b981', onClick: () => goToInstStatus('COMPLETED') },
            { label: 'Skipped', value: metrics.by_status?.SKIPPED || 0, color: '#94a3b8', onClick: () => goToInstStatus('SKIPPED') },
            { label: 'Failed', value: metrics.by_status?.FAILED || 0, color: '#ef4444', onClick: () => goToInstStatus('FAILED') },
          ]} />

          {/* Stats */}
          <KpiGrid columns={3} items={[
            { label: 'Avg Cycle Time', value: metrics.avg_cycle_seconds > 0 ? `${metrics.avg_cycle_seconds.toFixed(1)}s` : '--' },
            { label: 'Auto-Complete Rate', value: `${(metrics.auto_complete_rate * 100).toFixed(1)}%` },
            { label: 'Workflow Definitions', value: definitions.length },
          ]} />

          {/* Department breakdown */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 8, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 12, color: 'var(--text-hint)', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>
              Definitions by Department
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
              {sortedDepts.map(([dept, count], i) => (
                <div key={dept}
                  onClick={() => goToDefsDept(dept)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', padding: '8px 12px',
                    background: 'var(--bg-elevated)', borderRadius: 6,
                    cursor: 'pointer', transition: 'all 0.15s',
                    opacity: 0, animation: `fadeIn 0.3s var(--ease-default) both`,
                    animationDelay: `${i * 0.02}s`,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-overlay)'; e.currentTarget.style.transform = 'scale(1.02)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-elevated)'; e.currentTarget.style.transform = 'none'; }}
                >
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{cleanDeptName(dept)}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* DEFINITIONS TAB */}
      {activeTab === 'Definitions' && canvasWorkflow && (
        <WorkflowCanvas
          workflowCode={canvasWorkflow.workflow_code}
          workflowName={canvasWorkflow.workflow_name}
          department={canvasWorkflow.department}
          steps={canvasWorkflow.skill_sequence || []}
          onClose={() => setCanvasWorkflow(null)}
        />
      )}
      {activeTab === 'Definitions' && !canvasWorkflow && (
        <>
          <FilterBar filters={[
            { key: 'search', label: 'workflows', type: 'search', value: defSearch, onChange: setDefSearch, width: 200, placeholder: 'Search workflows...' },
            { key: 'dept', label: 'Department', type: 'select', value: defDept, onChange: setDefDept, options: departments.map(d => ({ value: d, label: cleanDeptName(d) })) },
          ]} />
          {defDept && (
            <div style={{ marginBottom: 12 }}>
              <ActionButton label="Clear filter" onClick={() => setDefDept('')} variant="ghost" size="sm" />
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--text-hint)', fontFamily: 'var(--font-mono)', marginBottom: 12, textAlign: 'right' }}>
            {filteredDefs.length} workflows
          </div>

          {/* When searching, show flat results */}
          {defSearch ? (
            <div style={{ overflowX: 'auto' }}>
              {filteredDefs.map(d => {
                const trigType = d.trigger_config?.type || 'MANUAL';
                const trigStyle = TRIGGER_STYLES[trigType] || TRIGGER_STYLES.MANUAL;
                return (
                  <div key={d.id}
                    onClick={() => setCanvasWorkflow(d)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                      borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{d.workflow_name}</div>
                      <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-hint)', marginTop: 1 }}>{d.workflow_code}</div>
                    </div>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{cleanDeptName(d.department)}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)' }}>{d.skill_sequence?.length || 0} steps</span>
                    <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, fontWeight: 600, background: trigStyle.bg, color: trigStyle.color }}>{trigType}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Grouped by department */
            <div>
              {(defDept ? [[defDept, groupedDefs[defDept]?.length || 0]] : sortedDepts)
                .filter(([dept]) => groupedDefs[dept as string])
                .map(([dept]) => {
                  const deptStr = dept as string;
                  const deptDefs = groupedDefs[deptStr] || [];
                  const isCollapsed = collapsedDepts.has(deptStr);
                  return (
                    <div key={deptStr} style={{ marginBottom: 8 }}>
                      {/* Department header */}
                      <div
                        onClick={() => toggleDept(deptStr)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                          background: 'var(--bg-card)', border: '1px solid var(--border-default)',
                          borderRadius: isCollapsed ? 8 : '8px 8px 0 0',
                          cursor: 'pointer', transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'var(--bg-surface)')}
                      >
                        <span style={{ fontSize: 10, color: 'var(--text-hint)', width: 12, textAlign: 'center' as const }}>
                          {isCollapsed ? '\u25B6' : '\u25BC'}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{cleanDeptName(deptStr)}</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>({deptDefs.length})</span>
                      </div>

                      {/* Workflow rows */}
                      {!isCollapsed && (
                        <div style={{ border: '1px solid var(--border-subtle)', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                          {deptDefs.map(d => {
                            const trigType = d.trigger_config?.type || 'MANUAL';
                            const trigStyle = TRIGGER_STYLES[trigType] || TRIGGER_STYLES.MANUAL;
                            return (
                              <div key={d.id}>
                                <div
                                  onClick={() => setCanvasWorkflow(d)}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px 8px 32px',
                                    borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer',
                                    transition: 'background 0.15s',
                                  }}
                                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                >
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)' }}>{d.workflow_name}</div>
                                  </div>
                                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', minWidth: 50, textAlign: 'center' as const }}>
                                    {d.skill_sequence?.length || 0} steps
                                  </span>
                                  <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, fontWeight: 600, background: trigStyle.bg, color: trigStyle.color, minWidth: 70, textAlign: 'center' as const }}>
                                    {trigType}
                                  </span>
                                </div>
                                {/* Expanded detail */}
                                {expandedDef === d.id && (
                                  <div style={{ padding: '8px 14px 12px 32px', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)' }}>
                                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-hint)', marginBottom: 4 }}>{d.workflow_code}</div>
                                    {d.description && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 8 }}>{d.description}</div>}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      {(d.skill_sequence || []).map((step: any, i: number) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', background: 'var(--bg-elevated)', borderRadius: 4 }}>
                                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-hint)', minWidth: 24 }}>#{step.step}</span>
                                          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--accent)' }}>{step.skill}</span>
                                          {step.route_human_review && (
                                            <span style={{ fontSize: 8, padding: '1px 4px', background: '#fffbeb', color: '#fcd34d', borderRadius: 3 }}>HUMAN GATE</span>
                                          )}
                                          <span style={{ fontSize: 9, color: 'var(--text-hint)', marginLeft: 'auto' }}>
                                            {step.on_success === null ? 'END' : `\u2192 ${step.on_success}`}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </>
      )}

      {/* INSTANCES TAB */}
      {activeTab === 'Instances' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <FilterBar filters={[
              { key: 'search', label: 'instances', type: 'search', value: instSearch, onChange: setInstSearch, width: 260, placeholder: 'Search by workflow name or ID...' },
              { key: 'status', label: 'Status', type: 'select', value: instStatus, onChange: setInstStatus, options: [
                { value: 'RUNNING', label: 'Running' },
                { value: 'PAUSED_HUMAN', label: 'Awaiting Review' },
                { value: 'COMPLETED', label: 'Completed' },
                { value: 'FAILED', label: 'Failed' },
              ]},
            ]} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {instStatus && <ActionButton label="Clear filter" onClick={() => setInstStatus('')} variant="ghost" size="sm" />}
              <ActionButton label="Refresh" onClick={() => { loadInstances(); loadMetrics(); }} variant="secondary" size="sm" />
              <span style={{ fontSize: 11, color: 'var(--text-hint)', fontFamily: 'var(--font-mono)' }}>
                {filteredInst.length} instances
              </span>
            </div>
          </div>

          <DataTable
            columns={instanceCols}
            rows={filteredInst}
            emptyMessage="No workflow instances found."
            onRowClick={(row: WorkflowInstance) => openWfPanel(row.id)}
            rowKey="id"
          />

          {/* Workflow Instance Detail Panel */}
          <DetailSheet
            open={wfPanelId !== null}
            onClose={() => { setWfPanelId(null); setWfPanelData(null); setWfPanelLogs([]); }}
            title={wfPanelData ? `Instance #${wfPanelId}` : 'Loading...'}
            subtitle={wfPanelData?.workflow_name || wfPanelData?.workflow_code || ''}
            statusBadge={wfPanelData?.status ? <StatusBadge status={wfPanelData.status} /> : undefined}
          >
            {wfPanelLoading ? (
              <DetailSkeleton />
            ) : wfPanelData ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Info grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {[
                    { label: 'Workflow', value: wfPanelData.workflow_name || wfPanelData.workflow_code },
                    { label: 'Department', value: wfNameMap[wfPanelData.workflow_id]?.dept ? cleanDeptName(wfNameMap[wfPanelData.workflow_id].dept) : '\u2014' },
                    { label: 'Started', value: wfPanelData.started_at ? new Date(wfPanelData.started_at).toLocaleString() : '\u2014' },
                    { label: 'Duration', value: wfPanelData.started_at && wfPanelData.completed_at
                      ? `${((new Date(wfPanelData.completed_at).getTime() - new Date(wfPanelData.started_at).getTime()) / 1000).toFixed(1)}s`
                      : '\u2014' },
                  ].map((m, i) => (
                    <div key={i} style={{ padding: 10, background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
                      <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{m.label}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginTop: 2 }}>{m.value}</div>
                    </div>
                  ))}
                </div>

                {/* Paused reason */}
                {wfPanelData.status === 'PAUSED_HUMAN' && wfPanelData.paused_reason && (
                  <div style={{ padding: '8px 12px', background: 'rgba(245,158,11,0.1)', borderRadius: 6, fontSize: 11, color: '#fcd34d' }}>
                    Paused: {wfPanelData.paused_reason}
                  </div>
                )}

                {/* Error */}
                {wfPanelData.status === 'FAILED' && wfPanelData.error_detail && (
                  <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: 6, fontSize: 11, color: '#fca5a5' }}>
                    Error: {wfPanelData.error_detail}
                  </div>
                )}

                {/* Execution Steps */}
                <div>
                  <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                    Execution Steps
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {(wfPanelLogs.length > 0 ? wfPanelLogs : (wfPanelData.step_results || [])).map((log: any, i: number) => {
                      const sc = STATUS_COLORS[log.status] || STATUS_COLORS.CANCELLED;
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--bg-elevated)', borderRadius: 4 }}>
                          <span style={{ fontSize: 12 }}>{STEP_ICONS[log.status] || '\u2B55'}</span>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-hint)', minWidth: 24 }}>
                            #{log.step_number || log.step || i + 1}
                          </span>
                          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--accent)', flex: 1 }}>
                            {log.skill_code || log.skill || `Step ${i + 1}`}
                          </span>
                          <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: sc.bg, color: sc.color }}>
                            {STATUS_LABELS[log.status] || log.status}
                          </span>
                          {log.execution_ms != null && (
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-hint)' }}>{log.execution_ms}ms</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Transaction ID */}
                {wfPanelData.transaction_id && (
                  <div style={{ fontSize: 9, color: 'var(--text-hint)', fontFamily: 'var(--font-mono)' }}>
                    Transaction: {wfPanelData.transaction_id}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                Failed to load instance details
              </div>
            )}
          </DetailSheet>
        </>
      )}

      {/* BUILDER TAB */}
      {activeTab === 'Builder' && (
        <WorkflowBuilder onClose={() => setActiveTab('Definitions')} />
      )}

      {/* TEST RUNNER TAB (admin-only) */}
      {activeTab === 'Test Runner' && isAdmin && (() => {
        const WorkflowTestRunner = require('./WorkflowTestRunner').default;
        return <WorkflowTestRunner />;
      })()}
    </PageLayout>
  );
}
